import { ATTENDANCE_LEAVE_TYPE } from '@src/models/interfaces/attendanceLeaveType';
import type {
  IWorkJourneyMatrixCell,
  TWorkJourneyMatrixCellCode,
  TWorkJourneyMatrixCellKind,
} from '@src/models/interfaces/workJourneyManagement.interface';

/**
 * DT glossary / product chip labels for matrix cells.
 * AUJ/AUI/PRE match the month-matrix mock codes.
 */
export const MATRIX_DT_DISPLAY: Record<TWorkJourneyMatrixCellCode, string> = {
  WORK: '✓',
  PRE: 'PRE',
  D: 'D',
  AUJ: 'AUJ',
  AUI: 'AUI',
  VAC: 'VAC',
  LM: 'L.M.',
  PGR: 'P.G.R.',
  PSG: 'P.S.G.R.',
  PREN: 'PREN.',
  POSTN: 'POSTN.',
  REM: 'REM',
  ABS: 'ABS',
};

/** Present chip for AR-backed work days — matches month matrix mock. */
export const MATRIX_PRESENT_DISPLAY = 'PRE';

export type MatrixCellVisualVariant =
  | 'empty'
  | 'planned'
  | 'present'
  | 'rest'
  | 'replacement_out'
  | TWorkJourneyMatrixCellCode;

export type MatrixCellVisual = {
  present: boolean;
  planned: boolean;
  code: TWorkJourneyMatrixCellCode | null;
  label: string;
  variant: MatrixCellVisualVariant;
};

export type MatrixCellVisualOptions = {
  /** Calendar dates that are rest days for this journey's workShift. */
  restDates?: Set<string>;
};

type LeaveMappedCell = {
  kind: TWorkJourneyMatrixCellKind;
  code: TWorkJourneyMatrixCellCode;
};

const HOLIDAY_OBSERVATION = 'holiday';

export const getCellObservation = (cell?: IWorkJourneyMatrixCell | null): string =>
  (cell?.observation ?? cell?.attendance?.observations ?? '').trim();

/**
 * Public holiday days arrive as justified leave (or ABS) with
 * `observation: holiday`. They still render as AUJ. Unknown exception types
 * mapped to `other_absence` + `ABS` without that observation are not holidays.
 */
export const isHolidayCell = (cell?: IWorkJourneyMatrixCell | null): boolean => {
  if (!cell) return false;
  return getCellObservation(cell) === HOLIDAY_OBSERVATION;
};

/** Maps attendance-record leaveType onto matrix absence chips. */
export const mapLeaveTypeToMatrixCell = (leaveType?: string | null): LeaveMappedCell | null => {
  const normalized = typeof leaveType === 'string' ? leaveType.trim() : leaveType;
  if (normalized === ATTENDANCE_LEAVE_TYPE.JUSTIFIED) {
    return { kind: 'justified_absence', code: 'AUJ' };
  }
  if (normalized === ATTENDANCE_LEAVE_TYPE.UNJUSTIFIED) {
    return { kind: 'unjustified_absence', code: 'AUI' };
  }
  return null;
};

export const getMatrixCellDisplayLabel = (
  cell?: IWorkJourneyMatrixCell,
  options?: MatrixCellVisualOptions,
): string => getMatrixCellVisual(cell, options).label;

export const isMatrixPresentCell = (
  cell?: IWorkJourneyMatrixCell,
  options?: MatrixCellVisualOptions,
): boolean => getMatrixCellVisual(cell, options).present;

const justifiedAbsenceVisual = (): MatrixCellVisual => ({
  present: false,
  planned: false,
  code: 'AUJ',
  label: MATRIX_DT_DISPLAY.AUJ,
  variant: 'AUJ',
});

const restDayVisual = (): MatrixCellVisual => ({
  present: false,
  planned: false,
  code: 'D',
  label: MATRIX_DT_DISPLAY.D,
  variant: 'rest',
});

/**
 * Justified leave chips (vacation, AUJ, paid leave, holiday, …) that should
 * yield to "D" on journey rest days. Outgoing cover (`replacement_out`) is
 * excluded — covering elsewhere keeps REM even on a rest day of this journey.
 */
export const isJustifiedAbsenceForRestDayOverride = (
  cell?: IWorkJourneyMatrixCell | null,
): boolean => {
  if (!cell) return false;
  if (cell.kind === 'replacement_out' || cell.kind === 'replacement') return false;
  if (cell.kind === 'unjustified_absence' || cell.code === 'AUI') return false;

  if (isHolidayCell(cell)) return true;

  return (
    cell.kind === 'justified_absence' ||
    cell.kind === 'vacation' ||
    cell.kind === 'medical_leave' ||
    cell.kind === 'paid_leave' ||
    cell.kind === 'unpaid_leave' ||
    cell.kind === 'prenatal' ||
    cell.kind === 'postnatal' ||
    cell.kind === 'other_absence' ||
    cell.code === 'AUJ' ||
    cell.code === 'VAC' ||
    cell.code === 'LM' ||
    cell.code === 'PGR' ||
    cell.code === 'PSG' ||
    cell.code === 'PREN' ||
    cell.code === 'POSTN' ||
    cell.code === 'ABS'
  );
};

/**
 * Resolves chip label + visual variant for a matrix cell.
 * Trusts backend `code` (PRE / AUJ / AUI / WORK / …) from leaveType + attendanceComplete.
 * Justified absences on journey rest days render as "D" (except replacement_out).
 */
export const getMatrixCellVisual = (
  cell?: IWorkJourneyMatrixCell,
  options?: MatrixCellVisualOptions,
): MatrixCellVisual => {
  if (!cell) {
    return { present: false, planned: false, code: null, label: '', variant: 'empty' };
  }

  // Covering elsewhere keeps the REM chip but never reads as present/planned
  // for this journey, so it must win over the attendance-backed branches.
  if (cell.kind === 'replacement_out') {
    return {
      present: false,
      planned: false,
      code: 'REM',
      label: MATRIX_DT_DISPLAY.REM,
      variant: 'replacement_out',
    };
  }

  // Vacation / AUJ / etc. on a non-working day of this journey → show rest "D".
  if (options?.restDates?.has(cell.date) && isJustifiedAbsenceForRestDayOverride(cell)) {
    return restDayVisual();
  }

  if (cell.code === 'PRE' || (cell.kind === 'work' && cell.code === 'WORK')) {
    return {
      present: true,
      planned: false,
      code: 'PRE',
      label: MATRIX_PRESENT_DISPLAY,
      variant: 'present',
    };
  }

  if (cell.kind === 'planned' || (cell.code === 'WORK' && cell.kind !== 'work')) {
    return {
      present: false,
      planned: true,
      code: 'WORK',
      label: MATRIX_DT_DISPLAY.WORK,
      variant: 'planned',
    };
  }

  if (cell.kind === 'rest' || cell.code === 'D') {
    return restDayVisual();
  }

  // Holiday is a justified absence for display — keep the AUJ chip.
  if (isHolidayCell(cell)) {
    return justifiedAbsenceVisual();
  }

  const observation = getCellObservation(cell);

  // AR leaveType can yield AUJ/AUI while `observation` still carries vacation.
  if (cell.code === 'AUJ' || cell.code === 'AUI') {
    if (observation === 'vacation') {
      return {
        present: false,
        planned: false,
        code: 'VAC',
        label: MATRIX_DT_DISPLAY.VAC,
        variant: 'VAC',
      };
    }
    return {
      present: false,
      planned: false,
      code: cell.code,
      label: MATRIX_DT_DISPLAY[cell.code],
      variant: cell.code,
    };
  }

  // Specific justified leave codes (P.G.R., L.M., …) keep their reason in the
  // tooltip, but the cell chip is always AUJ to match the matrix legend.
  if (
    cell.code === 'PGR' ||
    cell.code === 'LM' ||
    cell.code === 'PSG' ||
    cell.code === 'PREN' ||
    cell.code === 'POSTN' ||
    cell.kind === 'paid_leave' ||
    cell.kind === 'medical_leave' ||
    cell.kind === 'unpaid_leave' ||
    cell.kind === 'prenatal' ||
    cell.kind === 'postnatal'
  ) {
    return justifiedAbsenceVisual();
  }

  return {
    present: false,
    planned: false,
    code: cell.code,
    label: MATRIX_DT_DISPLAY[cell.code] ?? cell.code,
    variant: cell.code,
  };
};

/** True when the cell is painted as journey rest ("D") — no attendance drawer. */
export const isMatrixRestDayVisual = (
  cell?: IWorkJourneyMatrixCell | null,
  options?: MatrixCellVisualOptions,
): boolean => {
  const visual = getMatrixCellVisual(cell ?? undefined, options);
  return visual.variant === 'rest' || visual.label === 'D';
};
